import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

export default function VerifyAccount() {
  const location = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: location.state?.email || "",
    code: "",
  });

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: location.state?.message ? "success" : "",
    message: location.state?.message || "",
  });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.email.trim()) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!form.code.trim()) {
      newErrors.code = "Ingresa el código de verificación";
    } else if (!/^\d{5}$/.test(form.code.trim())) {
      newErrors.code = "El código debe tener 5 dígitos";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch("/api/gym/verify/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          code: form.code.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo verificar la cuenta";
        setErrors((prev) => ({
          ...prev,
          general: message,
        }));
        setBanner({
          type: "error",
          message,
        });
        return;
      }

      setBanner({
        type: "success",
        message: data.message || "Cuenta verificada correctamente",
      });

      setTimeout(() => {
        navigate("/login");
      }, 1600);
    } catch (error) {
      const message = "Ocurrió un error al conectar con el servidor";
      setErrors((prev) => ({
        ...prev,
        general: message,
      }));
      setBanner({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    const email = form.email.trim().toLowerCase();

    if (!email) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa tu correo electrónico",
      }));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa un correo válido",
      }));
      return;
    }

    setResending(true);

    try {
      const response = await fetch("/api/gym/resend-code/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo reenviar el código";
        setBanner({
          type: "error",
          message,
        });
        return;
      }

      setBanner({
        type: "success",
        message: data.message || "Te enviamos un nuevo código",
      });
    } catch (error) {
      setBanner({
        type: "error",
        message: "Ocurrió un error al conectar con el servidor",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#ffffff",
        py: { xs: 5, md: 7 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "90%", md: "75%" }, mx: "auto" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 4 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              pr: { md: 2 },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignSelf: { xs: "center", md: "flex-start" },
                px: 1.5,
                py: 0.7,
                borderRadius: 999,
                bgcolor: "rgba(229,9,20,0.08)",
                border: "1px solid rgba(229,9,20,0.12)",
                mb: 1.8,
              }}
            >
              <Typography
                sx={{
                  color: "#e50914",
                  fontWeight: 800,
                  fontSize: { xs: 12.8, md: 13.2 },
                  letterSpacing: "0.02em",
                }}
              >
                VERIFICACIÓN NOVAXFIT
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#111111",
                fontWeight: 900,
                lineHeight: 1.02,
                letterSpacing: -1.2,
                textAlign: { xs: "center", md: "left" },
                fontSize: {
                  xs: "clamp(34px, 8vw, 44px)",
                  md: "clamp(42px, 3.4vw, 58px)",
                },
                mb: 1.5,
              }}
            >
              Verifica tu cuenta para activar tu acceso
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.72)",
                lineHeight: 1.8,
                fontSize: { xs: 15.5, md: 16.5 },
                fontWeight: 500,
                textAlign: { xs: "center", md: "left" },
                maxWidth: 560,
                mx: { xs: "auto", md: 0 },
              }}
            >
              Te enviamos un código de 5 dígitos al correo que registraste.
              Ingresa ese código para activar tu cuenta en{" "}
              <Box
                component="strong"
                sx={{ fontWeight: 800, color: "#111111" }}
              >
                NovaXFit
              </Box>
              .
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              borderRadius: { xs: "24px", md: "28px" },
              border: "1px solid rgba(17,17,17,0.08)",
              bgcolor: "#ffffff",
              boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
              p: { xs: 2.2, sm: 3, md: 3.2 },
            }}
          >
            <Stack spacing={2}>
              {banner.message && (
                <Alert severity={banner.type} sx={{ borderRadius: "14px" }}>
                  {banner.message}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                error={Boolean(errors.email)}
                helperText={errors.email || " "}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Código de verificación"
                value={form.code}
                onChange={handleChange("code")}
                error={Boolean(errors.code)}
                helperText={errors.code || " "}
                variant="outlined"
                inputProps={{ maxLength: 5 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "16px",
                  py: 1.25,
                  fontSize: "1rem",
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                  boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    filter: "brightness(1.05)",
                    boxShadow: "0 22px 56px rgba(229,9,20,0.26)",
                  },
                  "&.Mui-disabled": {
                    color: "#ffffff",
                    opacity: 0.75,
                  },
                }}
              >
                {loading ? "Verificando..." : "Verificar cuenta"}
              </Button>

              <Button
                type="button"
                variant="outlined"
                fullWidth
                onClick={handleResendCode}
                disabled={resending}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "16px",
                  py: 1.2,
                  fontSize: "1rem",
                  color: "#111111",
                  borderColor: "rgba(17,17,17,0.12)",
                  bgcolor: "#ffffff",
                  "&:hover": {
                    borderColor: "rgba(229,9,20,0.32)",
                    bgcolor: "rgba(229,9,20,0.04)",
                    color: "#e50914",
                  },
                }}
              >
                {resending ? "Reenviando..." : "Reenviar código"}
              </Button>

              <Typography
                sx={{
                  textAlign: "center",
                  color: "rgba(17,17,17,0.72)",
                  fontSize: 15,
                  fontWeight: 500,
                }}
              >
                ¿Te equivocaste de correo?{" "}
                <Link
                  component={RouterLink}
                  to="/registro"
                  underline="none"
                  sx={{
                    color: "#e50914",
                    fontWeight: 800,
                    "&:hover": {
                      color: "#b30010",
                    },
                  }}
                >
                  Volver al registro
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}